import * as React from 'react';
import { ResponsivePieCanvas, PieCanvasProps } from '@nivo/pie';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames';
import toArray from 'lodash/toArray';

import { useTheme } from '../../../contexts/theme';
import { ColorStyleProps } from '../../../@types/theme';
import { parseChartColors } from '../../../utils/themeUtils';
import ChartTooltip from '../../atoms/ChartTooltip';
import BadgeWithIcon from '../../atoms/BadgeWithIcon';
import Spinner from '../../atoms/Spinner';

import styles from './PieChart.module.scss';

// Interfaces
// //////////////////////////

export interface ChartDatum {
  id: string;
  label: string;
  value: number;
  percentage?: number;
}

export type ChartOptions = Partial<PieCanvasProps<ChartDatum>>;

export interface Props extends React.HTMLAttributes<HTMLElement> {
  data: Array<ChartDatum>;
  options?: ChartOptions;
  height?: number;
  displayLegend?: boolean;
  loading?: boolean;
}

/**
 * Default chart options
 */
const defaultOptions: Partial<PieCanvasProps<ChartDatum>> = {
  margin: {
    top: 16,
    left: 16,
    right: 16,
    bottom: 16,
  },
  colors: { scheme: 'paired' },
  enableArcLinkLabels: false,
  arcLinkLabel: 'label',
  arcLinkLabelsSkipAngle: 10,
  arcLinkLabelsTextOffset: 6,
  arcLinkLabelsTextColor: '#343A40',
  arcLinkLabelsDiagonalLength: 16,
  arcLinkLabelsStraightLength: 24,
  arcLinkLabelsThickness: 1,
  arcLinkLabelsColor: 'inherit',
  arcLabelsSkipAngle: 18,
  arcLabelsRadiusOffset: 0.6,
  arcLabelsTextColor: {
    from: '#343A40',
    modifiers: [['darker', 3]],
  },
  tooltip: ({ datum }) => (
    <ChartTooltip
      datum={{
        ...datum,
        formattedValue: `${datum.data.value} (${datum.formattedValue})`,
      }}
      useFormattedValue
    />
  ),
  legends: [
    {
      anchor: 'bottom-right',
      direction: 'column',
      translateX: 100,
      itemWidth: 80,
      itemHeight: 20,
      itemTextColor: '#999',
      symbolSize: 12,
      symbolShape: 'circle',
      effects: [
        {
          on: 'hover',
          style: {
            itemTextColor: '#000',
          },
        },
      ],
    },
  ],
};

/**
 * <PieChart> component
 */
export const PieChart: React.FunctionComponent<Props> = ({
  className,
  data,
  loading,
  options = {},
  height = 220,
  displayLegend = false,
  ...htmlElementProps
}: Props) => {
  const [idxBlacklist, setIdxBlacklist] = React.useState<Array<number>>([]);

  const { theme } = useTheme();

  // Get chart keys
  const keys = React.useMemo(
    () => data.map((d) => ({ label: d.label, disabled: d.value === 0 })),
    [data],
  );

  /**
   * Get chart colors
   * We should make sure that it is redefined every time data changes, not only data.length
   */
  const colors = React.useMemo(
    () =>
      (options.colors
        ? toArray(options.colors)
        : theme.color.chart.slice(0, data.length)) as ColorStyleProps[],
    [data, options.colors, theme.color.chart],
  );

  // Merge user and default options
  const opts: ChartOptions = React.useMemo(
    () => ({
      ...defaultOptions,
      ...options,
      colors: parseChartColors(
        colors.filter((_c, idx) => !idxBlacklist.includes(idx)),
      ),
      legends: [],
      theme: {
        text: {
          fontFamily: theme.typography.fontFamily,
          fontWeight: 500,
          fontSize: 13,
          textColor: theme.color.blackLight,
        },
      },
    }),
    [options, colors, idxBlacklist, theme.typography, theme.color],
  );

  // Parse data accordingly to visible keys
  const parsedData = React.useMemo(() => {
    const filteredData = data.filter((_d, idx) => !idxBlacklist.includes(idx));
    const total = filteredData.reduce((acc, d) => acc + d.value, 0);
    return filteredData.map((d) => ({
      ...d,
      percentage: d.value / total,
    }));
  }, [data, idxBlacklist]);

  // Define what to do when chart legend is clicked
  const handleLegendClick = React.useCallback(
    (event: React.SyntheticEvent<HTMLElement>) => {
      const selectedKey = event.currentTarget.innerText;
      const keyIndex = keys.findIndex((k) => k.label === selectedKey);
      if (idxBlacklist.includes(keyIndex)) {
        setIdxBlacklist((old) => old.filter((i) => i !== keyIndex));
      } else {
        setIdxBlacklist((old) => [...old, keyIndex]);
      }
    },
    [keys, idxBlacklist],
  );

  return (
    <div
      className={classNames(styles.wrapper, className)}
      {...htmlElementProps}
    >
      <div className={styles.content}>
        {loading && <Spinner data-testid="piechart-spinner" />}

        {!loading && (
          <>
            <div
              style={{ height, width: height, flexShrink: 0 }}
              data-testid="chart-wrapper"
            >
              <ResponsivePieCanvas
                data={parsedData}
                value="percentage"
                valueFormat=" >-.0%"
                {...opts}
              />
            </div>

            {displayLegend && (
              <div className={styles['legend-wrapper']}>
                {keys.map((key, k) => (
                  <BadgeWithIcon
                    key={key.label}
                    className="mb-2"
                    content={key.label}
                    icon={faCheck}
                    color={colors[k]}
                    disabled={key.disabled}
                    onClick={handleLegendClick}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PieChart;
